/**
 * @file HttpRgsClient.ts
 * @role The real IRgsClient: owns the session lifecycle and orchestrates a spin. It validates
 *       every payload against the wire schemas in validators.ts (the true trust boundary — raw
 *       server bytes, not our own output), delegates HTTP to RgsTransport and the wire->DTO
 *       reshape to RgsSpinAdapter.
 * @owns Session state (id, currency, stake, paylines) and its persistence.
 * @reads RGS responses (unknown until validated), HttpRgsClientConfig.
 * @neverDoes Call fetch() directly (RgsTransport owns that seam). Decide outcomes or wins (the
 *            RGS does). Compute a balance. Touch Pixi. Use floats for money past the boundary.
 * @invariants 1, 7 (integer minor units), 10 (validate before use), 16 (HTTP only via
 *             RgsTransport), 13.
 * @see docs/BACKEND_CONTRACT.md, docs/RGS_SERVER_REQUIREMENTS.md, tests/fixtures/rgs/
 */
import { GameError } from '@core/errors/GameError';
import type {
  CreateSessionResponseDto,
  RestoreSessionResponseDto,
  SpinRequestDto,
  SpinResultDto,
} from './dto';
import type { IRgsClient, SpinCallOptions } from './IRgsClient';
import { adaptSpinResponse, toMajorUnits, toMinorUnits } from './RgsSpinAdapter';
import type { RgsSessionContext } from './RgsSpinAdapter';
import { RgsTransport } from './RgsTransport';
import {
  validateRgsHistoryResponse,
  validateRgsSessionResponse,
  validateRgsSpinResponse,
  type RgsSpinResponse,
  type RgsSessionResponse,
} from './validators';

/** Where the client remembers which session it owns, so a reload can rejoin it rather than
 *  silently abandoning a session (and its round history) on the server. */
export interface SessionStore {
  read(): string | null;
  write(sessionId: string): void;
  clear(): void;
  readAcknowledgedSequence?(sessionId: string): number | null;
  writeAcknowledgedSequence?(sessionId: string, sequence: number): void;
  readPendingSequence?(sessionId: string): number | null;
  writePendingSequence?(sessionId: string, sequence: number): void;
  clearPendingSequence?(sessionId: string): void;
}

export interface HttpRgsClientConfig {
  /** e.g. `https://rgs.mimetrikgames.com` — no trailing slash required. */
  baseUrl: string;
  /** The loaded RGS catalog id, e.g. `5x3-l2r-96`. */
  gameId: string;
  brand: string;
  userId: string;
  /** Minor-unit scale. Only used when the server does not send `currencyExponent` (pre-A4). */
  currencyExponent?: number;
  /** Symbol whose occurrences mark a feature trigger on the base screen. */
  scatterSymbolId?: string;
  requestTimeoutMs?: number;
  fetchImpl?: typeof fetch;
  sessionStore?: SessionStore;
}

const DEFAULT_CURRENCY_EXPONENT = 2;
const SESSION_STORAGE_KEY = 'slot-game-1.rgs';

/** Browser-backed session persistence; degrades to in-memory where storage is unavailable. */
function defaultSessionStore(gameId: string): SessionStore {
  let fallback: string | null = null;
  const sequenceFallback = new Map<string, number>();
  const pendingFallback = new Map<string, number>();
  const storage = ((): Storage | null => {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null; // storage blocked (private mode, sandboxed iframe)
    }
  })();

  const sessionKey = `${SESSION_STORAGE_KEY}.${gameId}.sessionId`;
  const sequenceKey = (sessionId: string): string =>
    `${SESSION_STORAGE_KEY}.${gameId}.${sessionId}.acknowledgedSequence`;
  const pendingKey = (sessionId: string): string =>
    `${SESSION_STORAGE_KEY}.${gameId}.${sessionId}.pendingSequence`;
  const readStoredNumber = (key: string): number | null => {
    if (!storage) return null;
    const value = storage.getItem(key);
    if (value === null) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
  };

  return {
    read: () => (storage ? storage.getItem(sessionKey) : fallback),
    write: (sessionId) => {
      if (storage) storage.setItem(sessionKey, sessionId);
      else fallback = sessionId;
    },
    clear: () => {
      const sessionId = storage ? storage.getItem(sessionKey) : fallback;
      if (storage) {
        storage.removeItem(sessionKey);
        if (sessionId) {
          storage.removeItem(sequenceKey(sessionId));
          storage.removeItem(pendingKey(sessionId));
        }
      } else {
        fallback = null;
        if (sessionId) {
          sequenceFallback.delete(sessionId);
          pendingFallback.delete(sessionId);
        }
      }
    },
    readAcknowledgedSequence: (sessionId) =>
      storage
        ? readStoredNumber(sequenceKey(sessionId))
        : (sequenceFallback.get(sessionId) ?? null),
    writeAcknowledgedSequence: (sessionId, sequence) => {
      if (storage) storage.setItem(sequenceKey(sessionId), String(sequence));
      else sequenceFallback.set(sessionId, sequence);
    },
    readPendingSequence: (sessionId) =>
      storage ? readStoredNumber(pendingKey(sessionId)) : (pendingFallback.get(sessionId) ?? null),
    writePendingSequence: (sessionId, sequence) => {
      if (storage) storage.setItem(pendingKey(sessionId), String(sequence));
      else pendingFallback.set(sessionId, sequence);
    },
    clearPendingSequence: (sessionId) => {
      if (storage) storage.removeItem(pendingKey(sessionId));
      else pendingFallback.delete(sessionId);
    },
  };
}

interface InFlightRound {
  request: SpinRequestDto;
  context: RgsSessionContext;
  baselineSequence: number;
}

export class HttpRgsClient implements IRgsClient {
  private readonly transport: RgsTransport;
  private readonly sessionStore: SessionStore;
  private context: RgsSessionContext | null = null;
  private acknowledgedSequence: number | null = null;
  private inFlightRound: InFlightRound | null = null;
  private recentlySettled: { requestId: string; result: SpinResultDto } | null = null;

  constructor(private readonly config: HttpRgsClientConfig) {
    this.sessionStore = config.sessionStore ?? defaultSessionStore(config.gameId);
    this.transport = new RgsTransport({
      baseUrl: config.baseUrl,
      brand: config.brand,
      userId: config.userId,
      ...(config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}),
      // The transport detects a session the server has forgotten; forgetting it here is this
      // class's job, since it is the one holding the session.
      onSessionExpired: () => {
        this.sessionStore.clear();
        this.context = null;
        this.acknowledgedSequence = null;
        this.inFlightRound = null;
        this.recentlySettled = null;
      },
    });
  }

  async createSession(): Promise<CreateSessionResponseDto> {
    const raw = await this.transport.request('POST', `/games/${this.config.gameId}/sessions`, {
      failureCode: 'SESSION_CREATE_FAILED',
      body: {},
    });
    const created = this.adoptSession(validateRgsSessionResponse(raw));
    this.acknowledgeSequence(0);
    return created;
  }

  /**
   * Rejoin the session this client already owns, if the server still has it. The RGS closes any
   * other active session when a new one is created, so blindly calling createSession() on every
   * boot would orphan the previous session and its history.
   *
   * The deployed RGS has no request idempotency key. A stored sequence cursor plus the newest
   * history entry identifies a round that settled after the last response this client received.
   */
  async restoreSession(): Promise<RestoreSessionResponseDto> {
    const knownSessionId = this.sessionStore.read();
    if (!knownSessionId) {
      return { ...(await this.createSession()), hasOpenRound: false };
    }

    const raw = await this.transport.request(
      'GET',
      `/games/${this.config.gameId}/sessions/${knownSessionId}`,
      { failureCode: 'SESSION_CREATE_FAILED', allowNotFound: true },
    );
    if (raw === null) {
      this.sessionStore.clear();
      return { ...(await this.createSession()), hasOpenRound: false };
    }

    const session = validateRgsSessionResponse(raw);
    if (session.status !== 'Active') {
      this.sessionStore.clear();
      return { ...(await this.createSession()), hasOpenRound: false };
    }

    const restored = this.adoptSession(session);
    const context = this.requireContext();
    const storedSequence = this.sessionStore.readAcknowledgedSequence?.(session.id) ?? null;
    const pendingSequence = this.sessionStore.readPendingSequence?.(session.id) ?? null;
    const latest = await this.latestHistoryRound(context);

    if (pendingSequence !== null && (!latest || latest.sequenceNumber <= pendingSequence)) {
      // The previous connection disappeared without a settled history row. Start a new session,
      // which closes the old one and fences a stale paid request before another wager is allowed.
      this.sessionStore.clear();
      return { ...(await this.createSession()), hasOpenRound: false };
    }

    if (storedSequence === null) {
      // First boot after installing sequence-aware recovery: establish a cursor without replaying
      // an old round the player may already have seen under a previous frontend version.
      this.acknowledgeSequence(latest?.sequenceNumber ?? 0);
      return { ...restored, hasOpenRound: false };
    }
    this.acknowledgedSequence = storedSequence;
    if (!latest || latest.sequenceNumber === storedSequence) {
      this.sessionStore.clearPendingSequence?.(session.id);
      return { ...restored, hasOpenRound: false };
    }
    if (latest.sequenceNumber < storedSequence) {
      throw new GameError(
        'SESSION_CREATE_FAILED',
        `RGS history sequence moved backwards from ${storedSequence} to ${latest.sequenceNumber}.`,
      );
    }
    this.assertNextSequence(latest, storedSequence);

    const openRound = this.adaptHistoryRound(latest, context);
    this.acknowledgeSequence(latest.sequenceNumber);
    this.sessionStore.clearPendingSequence?.(session.id);
    return { ...restored, hasOpenRound: true, openRound };
  }

  async spin(request: SpinRequestDto, options?: SpinCallOptions): Promise<SpinResultDto> {
    if (this.inFlightRound) {
      throw new GameError(
        'ROUND_STATUS_UNKNOWN',
        'The previous round is still being reconciled. Reload to reconnect safely before playing again.',
      );
    }
    const context = await this.syncBet(request.betAmountMinor);
    const baselineSequence = await this.ensureSequenceCursor(context);
    this.recentlySettled = null;
    this.inFlightRound = { request, context, baselineSequence };
    this.sessionStore.writePendingSequence?.(context.sessionId, baselineSequence);

    try {
      const raw = await this.transport.request(
        'POST',
        `/games/${this.config.gameId}/sessions/${context.sessionId}/play`,
        {
          failureCode: 'SPIN_REQUEST_FAILED',
          // Production never forces RNG output. The empty object matches PlayRequest while omitting
          // its development-only `sequence` field.
          body: {},
          ...(options?.signal ? { signal: options.signal } : {}),
        },
      );
      const wire = validateRgsSpinResponse(raw);
      this.assertNextSequence(wire, baselineSequence);
      const result = this.adaptRound(wire, request, context);
      this.acknowledgeSequence(wire.sequenceNumber);
      this.sessionStore.clearPendingSequence?.(context.sessionId);
      this.inFlightRound = null;
      this.recentlySettled = { requestId: request.requestId, result };
      return result;
    } catch (error) {
      const safelyRejected =
        error instanceof GameError &&
        ['INSUFFICIENT_BALANCE', 'SESSION_EXPIRED', 'SPIN_REQUEST_FAILED'].includes(error.code);
      if (safelyRejected) {
        this.sessionStore.clearPendingSequence?.(context.sessionId);
        this.inFlightRound = null;
      }
      throw error;
    }
  }

  /** Resolve an aborted/lost `/play` response from the authoritative newest history row. */
  async recoverRound(
    request: SpinRequestDto,
    options?: SpinCallOptions,
  ): Promise<SpinResultDto | null> {
    if (this.recentlySettled?.requestId === request.requestId) {
      return this.recentlySettled.result;
    }
    const pending = this.inFlightRound;
    if (!pending || pending.request.requestId !== request.requestId) return null;

    const latest = await this.latestHistoryRound(pending.context, options?.signal);
    if (!latest || latest.sequenceNumber <= pending.baselineSequence) return null;
    this.assertNextSequence(latest, pending.baselineSequence);

    const result = this.adaptRound(latest, pending.request, pending.context);
    this.acknowledgeSequence(latest.sequenceNumber);
    this.sessionStore.clearPendingSequence?.(pending.context.sessionId);
    this.inFlightRound = null;
    this.recentlySettled = { requestId: request.requestId, result };
    return result;
  }

  // --- session -------------------------------------------------------------

  private adoptSession(session: RgsSessionResponse): CreateSessionResponseDto {
    const currencyExponent =
      session.currencyExponent ?? this.config.currencyExponent ?? DEFAULT_CURRENCY_EXPONENT;

    this.context = {
      sessionId: session.id,
      currency: session.currency,
      currencyExponent,
      betAmountMinor: session.betAmountMinor ?? toMinorUnits(session.betAmount, currencyExponent),
      paylines: new Map(session.paylines.map((line) => [line.id, line.rowPerReel])),
    };
    this.sessionStore.write(session.id);

    return {
      sessionId: session.id,
      gameId: session.gameCode,
      currency: session.currency,
      balanceMinor: session.balanceMinor ?? toMinorUnits(session.balance, currencyExponent),
      betAmountMinor: session.betAmountMinor ?? toMinorUnits(session.betAmount, currencyExponent),
      ...(session.activeCampaignRoundsRemaining !== undefined
        ? { activeCampaignRoundsRemaining: session.activeCampaignRoundsRemaining }
        : {}),
      ...(session.pendingPromoFreeSpinsCount !== undefined
        ? { pendingPromoFreeSpinsCount: session.pendingPromoFreeSpinsCount }
        : {}),
    };
  }

  private async ensureSequenceCursor(context: RgsSessionContext): Promise<number> {
    if (this.acknowledgedSequence !== null) return this.acknowledgedSequence;
    const latest = await this.latestHistoryRound(context);
    const sequence = latest?.sequenceNumber ?? 0;
    this.acknowledgeSequence(sequence);
    return sequence;
  }

  private acknowledgeSequence(sequence: number): void {
    const context = this.requireContext();
    this.acknowledgedSequence = sequence;
    this.sessionStore.writeAcknowledgedSequence?.(context.sessionId, sequence);
  }

  private async latestHistoryRound(
    context: RgsSessionContext,
    signal?: AbortSignal,
  ): Promise<RgsSpinResponse | null> {
    const raw = await this.transport.request(
      'GET',
      `/games/${this.config.gameId}/sessions/${context.sessionId}/history?page=1&pageSize=1`,
      { failureCode: 'SPIN_REQUEST_FAILED', ...(signal ? { signal } : {}) },
    );
    return validateRgsHistoryResponse(raw).items[0] ?? null;
  }

  private adaptHistoryRound(wire: RgsSpinResponse, context: RgsSessionContext): SpinResultDto {
    const exponent = wire.currencyExponent ?? context.currencyExponent;
    return this.adaptRound(
      wire,
      {
        requestId: `history:${context.sessionId}:${wire.sequenceNumber}`,
        sessionId: context.sessionId,
        gameId: this.config.gameId,
        betAmountMinor: wire.betAmountMinor ?? toMinorUnits(wire.betAmount, exponent),
        currency: wire.currency ?? context.currency,
        mode: 'base',
        clientTime: wire.createdAt,
      },
      context,
    );
  }

  private adaptRound(
    wire: RgsSpinResponse,
    request: SpinRequestDto,
    context: RgsSessionContext,
  ): SpinResultDto {
    return adaptSpinResponse(wire, request, context, {
      ...(this.config.scatterSymbolId ? { scatterSymbolId: this.config.scatterSymbolId } : {}),
    });
  }

  private assertNextSequence(wire: RgsSpinResponse, baselineSequence: number): void {
    if (wire.sequenceNumber !== baselineSequence + 1) {
      throw new GameError(
        'SPIN_RESPONSE_INVALID',
        `Expected RGS sequence ${baselineSequence + 1}, got ${wire.sequenceNumber}.`,
      );
    }
  }

  /**
   * Push the player's stake onto the session before the round is played.
   *
   * The RGS settles a play at the SESSION's `betAmount`; a `betAmount` sent in the play body is
   * ignored (verified against the deployed
   * server). Without this the server would settle at a stake the player never chose — so the
   * client would be showing a bet the wallet was not charged, and `validateSpinResultForContext`
   * rejects the round on `bet mismatch` rather than let that reach the player.
   *
   * A no-op when the stake already matches, so a run of same-bet spins costs no extra request.
   * An unsupported level comes back as 400 `Invalid bet` and maps to a recoverable failure.
   */
  private async syncBet(betAmountMinor: number): Promise<RgsSessionContext> {
    const context = this.requireContext();
    if (betAmountMinor === context.betAmountMinor) return context;

    const raw = await this.transport.request(
      'PUT',
      `/games/${this.config.gameId}/sessions/${context.sessionId}`,
      {
        failureCode: 'SPIN_REQUEST_FAILED',
        body: { betAmount: toMajorUnits(betAmountMinor, context.currencyExponent) },
      },
    );
    this.adoptSession(validateRgsSessionResponse(raw));
    return this.requireContext();
  }

  private requireContext(): RgsSessionContext {
    if (!this.context) {
      throw new GameError('SESSION_EXPIRED', 'No RGS session; call createSession() first.');
    }
    return this.context;
  }
}
